import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Button, VStack, Input, Text, FormControl, FormErrorMessage,
    InputGroup, InputRightElement, InputLeftAddon, PinInput, PinInputField, HStack
} from '@chakra-ui/react';
import StepHeader from './StepHeader';
import { verifyotp, checkavailability, sendotp } from '../../collection/ApiCollection';
import { toast } from 'react-toastify';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { cousntryCodesArr } from '../../utils/constants';
import { Select as AntdSelect } from 'antd';
import { updateSignUpData, nextStep } from '../../store/formSlice';

// Utility function outside the component
const getContactType = (value) => {
    if (/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value)) return 'email';
    if (/^\d{7,15}$/.test(value.replace(/\D/g, ''))) return 'phone';
    return null;
};

const initialState = {
    fullName: '',
    contact: '',
    countryCode: '+1',
    password: '',
    confirmPassword: '',
    otp: ''
};

const initialErrors = {
    fullName: '',
    contact: '',
    password: '',
    confirmPassword: '',
    otp: ''
};

const YourDetailsStep = React.memo(() => {
    const dispatch = useDispatch();
    const { signUpData } = useSelector((state) => state.form);

    const [fields, setFields] = useState({ ...initialState, ...signUpData });
    const [errors, setErrors] = useState(initialErrors);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showOtpField, setShowOtpField] = useState(false);
    const [isOtpVerified, setIsOtpVerified] = useState(false);
    const [getOtpLoading, setGetOtpLoading] = useState(false);
    const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFields((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
        if (name === 'contact') setShowOtpField(false); // Reset OTP if contact changes
    }, [errors]);

    const handleCountryCodeChange = useCallback((value) => {
        setFields((prev) => ({ ...prev, countryCode: value }));
    }, []);

    const validate = useCallback(() => {
        let valid = true;
        let newErrors = { ...initialErrors };
        if (!fields.fullName.trim()) {
            newErrors.fullName = 'Name is required';
            valid = false;
        }
        if (!fields.contact.trim()) {
            newErrors.contact = 'Email or phone is required';
            valid = false;
        } else if (!getContactType(fields.contact.trim())) {
            newErrors.contact = 'Enter a valid email or phone number';
            valid = false;
        }
        if (!fields.password) {
            newErrors.password = 'Password is required';
            valid = false;
        } else if (fields.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
            valid = false;
        } else if (!/(?=.*[A-Za-z])(?=.*\d)/.test(fields.password)) {
            newErrors.password = 'Password must contain at least one letter and one number';
            valid = false;
        }
        if (!fields.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
            valid = false;
        } else if (fields.password !== fields.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
            valid = false;
        }
        if (showOtpField && (!fields.otp || fields.otp.length !== 4)) {
            newErrors.otp = 'Enter the 4-digit OTP';
            valid = false;
        }
        setErrors(newErrors);
        return valid;
    }, [fields, showOtpField]);

    const handleGetOtp = useCallback(async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setGetOtpLoading(true);
        const trimmedContact = fields.contact.trim();
        const contactType = getContactType(trimmedContact);
        try {
            // 1. Check availability
            const params = new URLSearchParams();
            if (contactType === 'phone') {
                params.append('phoneNumber', trimmedContact);
                params.append('countryCode', fields.countryCode);
            } else {
                params.append('email', trimmedContact);
            }
            await checkavailability(params.toString());
            // 2. Send OTP
            let payload;
            if (contactType === 'phone') {
                payload = { phoneNumber: trimmedContact, countryCode: fields.countryCode, type: 'sms' };
            } else {
                payload = { email: trimmedContact, type: 'email' };
            }
            await sendotp(payload);
            toast.success('OTP sent!');
            setShowOtpField(true);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.error || err?.message || 'Something went wrong');
        } finally {
            setGetOtpLoading(false);
        }
    }, [fields, validate]);

    const handleVerifyOtp = useCallback(async (e) => {
        e.preventDefault();
        if (!fields.otp || fields.otp.length !== 4) {
            setErrors((prev) => ({ ...prev, otp: 'Enter the 4-digit OTP' }));
            return;
        }
        setVerifyOtpLoading(true);
        const trimmedContact = fields.contact.trim();
        const contactType = getContactType(trimmedContact);
        let payload;
        if (contactType === 'email') {
            payload = { email: trimmedContact, otp: fields.otp, type: 'email' };
        } else {
            payload = { phoneNumber: trimmedContact, countryCode: fields.countryCode, otp: fields.otp, type: 'sms' };
        }
        try {
            await verifyotp(payload);
            toast.success('OTP verified!');
            setIsOtpVerified(true);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.error || err?.message || 'OTP verification failed');
        } finally {
            setVerifyOtpLoading(false);
        }
    }, [fields]);

    const handleContinue = useCallback((e) => {
        e.preventDefault();
        if (isOtpVerified) {
            const contactType = getContactType(fields.contact.trim());
            const signUpPayload = {
                fullName: fields.fullName,
                password: fields.password,
                confirmPassword: fields.confirmPassword,
                ...(contactType === 'email' && { email: fields.contact.trim() }),
                ...(contactType === 'phone' && {
                    phoneNumber: fields.contact.trim(),
                    countryCode: fields.countryCode,
                }),
            };
            dispatch(updateSignUpData(signUpPayload));
            dispatch(nextStep());
        }
    }, [fields, isOtpVerified, dispatch]);

    const isPhone = getContactType(fields.contact) === 'phone';

    return (
        <Box maxW={{ base: '100%', md: '3xl' }} mx="auto" p={{ base: 2, md: 0 }} textAlign="center" bg="transparent">
            <StepHeader title="Your Details" subtitle="Enter Your Details" />
            <form onSubmit={handleContinue}>
                <VStack spacing={4} mb={4} mx={4} align="stretch">
                    <FormControl isInvalid={!!errors.fullName} w="100%" maxW="500px" mx="auto">
                        <Input
                            id="fullName"
                            name="fullName"
                            placeholder="What's your name?"
                            value={fields.fullName}
                            onChange={handleChange}
                            w="100%"
                        />
                        <FormErrorMessage>{errors.fullName}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors.contact} w="100%" maxW="500px" mx="auto">
                        <InputGroup w="100%">
                            {isPhone && (
                                <InputLeftAddon>
                                    <AntdSelect
                                        value={fields.countryCode}
                                        onChange={handleCountryCodeChange}
                                        style={{ width: 100, _focus: { border: '1px solid', borderColor: '#108084', boxShadow: '0 0 2px #108084' } }}
                                        dropdownStyle={{ zIndex: 1300 }}
                                    >
                                        {cousntryCodesArr.map((c) => (
                                            <AntdSelect.Option key={c.code + c.name} value={c.code}>
                                                {c.name} ({c.code})
                                            </AntdSelect.Option>
                                        ))}
                                    </AntdSelect>
                                </InputLeftAddon>
                            )}
                            <Input
                                id="contact"
                                name="contact"
                                placeholder="Your email or phone number?"
                                value={fields.contact}
                                onChange={handleChange}
                                w="100%"
                            />
                        </InputGroup>
                        <FormErrorMessage>{errors.contact}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors.password} w="100%" maxW="500px" mx="auto">
                        <InputGroup w="100%">
                            <Input
                                id="password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Choose a password"
                                value={fields.password}
                                onChange={handleChange}
                                w="100%"
                            />
                            <InputRightElement width='4.5rem'>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    h='1.75rem'
                                    onClick={() => setShowPassword((show) => !show)}
                                    tabIndex={-1}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                </Button>
                            </InputRightElement>
                        </InputGroup>
                        <FormErrorMessage>{errors.password}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors.confirmPassword} w="100%" maxW="500px" mx="auto">
                        <InputGroup w="100%">
                            <Input
                                id="confirmPassword"
                                name="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="Confirm password"
                                value={fields.confirmPassword}
                                onChange={handleChange}
                                w="100%"
                            />
                            <InputRightElement width='4.5rem'>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    h='1.75rem'
                                    onClick={() => setShowConfirmPassword((show) => !show)}
                                    tabIndex={-1}
                                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                >
                                    {showConfirmPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                </Button>
                            </InputRightElement>
                        </InputGroup>
                        <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
                    </FormControl>
                    {showOtpField && (
                        <FormControl isInvalid={!!errors.otp} w="100%" maxW="500px" mx="auto">
                            <HStack>
                                <PinInput
                                    id="otp"
                                    name="otp"
                                    focusBorderColor="brand.500"
                                    value={fields.otp}
                                    otp
                                    onChange={value => {
                                        setFields(prev => ({ ...prev, otp: value }));
                                        if (errors.otp) setErrors(prev => ({ ...prev, otp: '' }));
                                    }}
                                >
                                    <PinInputField />
                                    <PinInputField />
                                    <PinInputField />
                                    <PinInputField />
                                </PinInput>
                            </HStack>
                            <FormErrorMessage>{errors.otp}</FormErrorMessage>
                        </FormControl>
                    )}
                </VStack>
                <Text fontSize="16px" color="black" mb={4}>
                    We'll keep you updated and send you call summaries. Your privacy is fully protected.
                </Text>
                {!showOtpField && (
                    <Button
                        mt={4}
                        mx={2}
                        px="30px"
                        isLoading={getOtpLoading}
                        onClick={handleGetOtp}
                    >
                        Get OTP
                    </Button>
                )}
                {showOtpField && (
                    <Button
                        mt={4}
                        mx={2}
                        px="30px"
                        isLoading={verifyOtpLoading}
                        onClick={handleVerifyOtp}
                        isDisabled={!fields.otp || isOtpVerified}
                    >
                        Verify
                    </Button>
                )}
                <Button
                    type="submit"
                    mt={4}
                    mx={2}
                    px="30px"
                    isDisabled={!isOtpVerified}
                >
                    Continue
                </Button>
            </form>
        </Box>
    );
});

export default YourDetailsStep;